import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Download, Loader2, ZoomIn, ZoomOut, Maximize2, RotateCw, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface EnhancedPDFPreviewProps {
  file?: File | null;
  url?: string | null;
  className?: string;
}

export function EnhancedPDFPreview({ file, url, className }: EnhancedPDFPreviewProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [usePDFJS, setUsePDFJS] = useState(true);
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { session } = useAuth();

  // Cleanup function
  const cleanup = useCallback(() => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  useEffect(() => {
    if (file) {
      console.log('EnhancedPDFPreview: File received:', file.name, file.type, file.size);
      setIsLoading(true);
      setError(null);
      setPdfLoadError(false);
      
      // Create blob URL for uploaded file
      const objectUrl = URL.createObjectURL(file);
      console.log('EnhancedPDFPreview: Created blob URL:', objectUrl);
      setPreviewUrl(objectUrl);
      setIsLoading(false);
      
      return () => {
        console.log('EnhancedPDFPreview: Cleaning up blob URL');
        URL.revokeObjectURL(objectUrl);
        setPreviewUrl(null);
      };
    } else if (url) {
      console.log('EnhancedPDFPreview: URL received:', url);
      setPreviewUrl(url);
    } else {
      console.log('EnhancedPDFPreview: No file or URL');
      setPreviewUrl(null);
    }
  }, [file, url]);

  const handleDownload = () => {
    if (file) {
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else if (previewUrl) {
      window.open(previewUrl, '_blank');
    }
  };

  const handleFullScreen = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank');
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    console.log('EnhancedPDFPreview: PDF loaded successfully, pages:', numPages);
    setTotalPages(numPages);
    setCurrentPage(1);
    setPdfLoadError(false);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('EnhancedPDFPreview: PDF load error:', error);
    setPdfLoadError(true);
    setError('Failed to load PDF with PDF.js. Trying fallback...');
  };

  const onPageLoadSuccess = () => {
    console.log('EnhancedPDFPreview: Page loaded successfully');
  };

  const onPageLoadError = (error: Error) => {
    console.error('EnhancedPDFPreview: Page load error:', error);
    setPdfLoadError(true);
  };

  if (!file && !url) {
    return (
      <Card className={className}>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <p className="text-slate-500">No document to preview</p>
        </CardContent>
      </Card>
    );
  }

  if (error && !usePDFJS) {
    return (
      <Card className={`border-red-300 bg-red-50 ${className}`}>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <AlertTriangle className="h-16 w-16 text-red-400 mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setError(null)}>
              Retry
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download Instead
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-2" />
          <p className="text-slate-600">Loading PDF...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="p-3 border-b bg-slate-50">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">Bill Document</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setZoom(Math.max(50, zoom - 25))}
              disabled={zoom <= 50}
              className="h-7 w-7 p-0"
            >
              <ZoomOut className="h-3 w-3" />
            </Button>
            <span className="text-xs font-medium min-w-[45px] text-center">
              {zoom}%
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setZoom(Math.min(200, zoom + 25))}
              disabled={zoom >= 200}
              className="h-7 w-7 p-0"
            >
              <ZoomIn className="h-3 w-3" />
            </Button>
            <div className="w-px h-4 bg-slate-300 mx-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRotation((rotation + 90) % 360)}
              className="h-7 w-7 p-0"
            >
              <RotateCw className="h-3 w-3" />
            </Button>
            <div className="w-px h-4 bg-slate-300 mx-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUsePDFJS(!usePDFJS)}
              className="h-7 px-2 text-xs"
            >
              {usePDFJS ? 'Iframe' : 'PDF.js'}
            </Button>
            <div className="w-px h-4 bg-slate-300 mx-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={handleFullScreen}
              className="h-7 w-7 p-0"
            >
              <Maximize2 className="h-3 w-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="h-7 px-2"
            >
              <Download className="h-3 w-3 mr-1" />
              <span className="text-xs">Download</span>
            </Button>
          </div>
        </div>
        {totalPages > 0 && (
          <div className="flex items-center justify-center gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={prevPage}
              disabled={currentPage <= 1}
              className="h-6 px-2"
            >
              <ChevronLeft className="h-3 w-3" />
            </Button>
            <span className="text-xs text-slate-600">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={nextPage}
              disabled={currentPage >= totalPages}
              className="h-6 px-2"
            >
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        )}
      </CardHeader>

      <CardContent className="p-0">
        <div className="relative bg-slate-100 flex items-center justify-center" style={{ height: '650px' }}>
          {usePDFJS && !pdfLoadError ? (
            <div className="overflow-auto h-full w-full">
              <Document
                file={previewUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                }
                error={
                  <div className="flex flex-col items-center justify-center h-full">
                    <AlertTriangle className="h-16 w-16 text-red-400 mb-4" />
                    <p className="text-red-600 mb-4">PDF failed to load with PDF.js</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUsePDFJS(false)}
                    >
                      Try Iframe Fallback
                    </Button>
                  </div>
                }
              >
                <Page
                  pageNumber={currentPage}
                  scale={zoom / 100}
                  rotate={rotation}
                  onLoadSuccess={onPageLoadSuccess}
                  onLoadError={onPageLoadError}
                  className="shadow-lg"
                />
              </Document>
            </div>
          ) : (
            <div className="overflow-auto h-full w-full">
              <iframe
                ref={iframeRef}
                src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                className="w-full h-full border-0"
                title="PDF Preview"
                onError={() => {
                  console.log('EnhancedPDFPreview: Iframe failed to load');
                  setError('PDF failed to load with iframe');
                }}
                onLoad={() => console.log('EnhancedPDFPreview: Iframe loaded successfully')}
                style={{ minHeight: '800px' }}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
